// Global Instructions Rule Applied!
//
// Configuration → Agent Gateway (admin only).
//
// The volume limits for the MCP gateway — the OAuth front door third-party AI
// agents connect through. Until this panel existed, changing one meant an
// UPDATE plus a backend restart (docs/AGENT_ACCESS_DEPLOYMENT.md §4G.2).
//
// Three sections:
//   1. Products — cfg.agents. Requests per minute and rows per day, enforced
//      PER GRANT by agent_usage_consume, plus the product kill switch.
//   2. Tools    — cfg.agent_tools. The per-call caps bag (max_rows,
//      max_results, …) and the per-tool enable.
//   3. Usage today — read-only, from public.agent_usage_counters.
//
// Deliberately NOT here: allowed_modes / write_tables. Which tools may run a
// write live rather than as a proposal is consent policy, not a volume limit,
// and keeping it out means the caps editor cannot disturb it.
//
// Chrome copied from DocumentsPanel.tsx; the product identity cell, Source tag
// and the Approve/Disable Popconfirm pair are copied verbatim from
// the Connected Agents "Agent products" tab, which this panel replaced
// outright (owner 2026-09-02) — the two listed the same cfg.agents rows with
// the same actions, and product approval belongs in an administrator's area.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Popconfirm, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { ReloadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import InlineNumberField from './InlineNumberField.tsx'
import JsonFieldsModal, { JsonFieldSpec } from './JsonFieldsModal.tsx'
import {
  AgentConfigValueKey,
  AgentGatewayConfigAdminData,
  AgentGatewayUsageData,
  AgentProductAdminRow,
  AgentToolAdminRow
} from '../../../state_manager/data_models/agent_gateway_config_model.ts'
import {
  fetchAgentGatewayConfig,
  fetchAgentGatewayUsage,
  updateAgentProduct,
  updateAgentTool
} from '../services/agentGatewayConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const EMPTY_CONFIG: AgentGatewayConfigAdminData = {
  resolvedEnvironment: 'global',
  products: [],
  tools: [],
  capKeys: []
}

const EMPTY_USAGE: AgentGatewayUsageData = { by_agent: [], by_grant: [], recent_minutes: [] }

const compactNumber = (v?: number | null): string => Number(v ?? 0).toLocaleString()

/** Compact one-line summary of a caps bag for a table cell. */
function capsSummary(bag: unknown): string {
  if (!bag || typeof bag !== 'object') return ''
  const entries = Object.entries(bag as Record<string, unknown>)
  if (!entries.length) return ''
  return entries.map(([k, v]) => `${k} ${String(v)}`).join(' · ')
}

export default function AgentGatewayPanel(): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)

  const [data, setData] = useState<AgentGatewayConfigAdminData>(EMPTY_CONFIG)
  const [usage, setUsage] = useState<AgentGatewayUsageData>(EMPTY_USAGE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [capsRow, setCapsRow] = useState<AgentToolAdminRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [config, usageData] = await Promise.all([
        fetchAgentGatewayConfig(token()),
        fetchAgentGatewayUsage(token(), 1)
      ])
      setData(config)
      setUsage(usageData)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load agent gateway configuration')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // reloadOnError: a controlled Switch keeps its flipped DOM state until a
  // re-render arrives, so a refused write must trigger one or the toggle lies.
  const run = async (
    action: () => Promise<void>,
    failure: string,
    reloadOnError = false
  ): Promise<boolean> => {
    try {
      await action()
      await load()
      return true
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : failure)
      if (reloadOnError) await load()
      return false
    }
  }

  // ---- products -----------------------------------------------------------
  // 0 means uncapped, so the cell says so in words. `uncapped`, lowercase, is
  // the exact wording the retired Agent products tab used — the same fact must not read two
  // different ways on two screens.
  const capCell = (
    value: number | null | undefined,
    row: AgentProductAdminRow,
    field: 'requests_per_minute' | 'daily_row_limit',
    max: number,
    failure: string
  ): React.ReactElement => (
    <span className='flex items-center gap-2'>
      <InlineNumberField
        value={value ?? 0}
        min={0}
        max={max}
        width={100}
        onCommit={(next) =>
          run(
            () => updateAgentProduct(token(), row.agents__agent_key, { [field]: next ?? 0 }),
            failure,
            true
          )
        }
      />
      {(value ?? 0) === 0 ? (
        <Text type='secondary' style={{ fontSize: 12 }}>
          uncapped
        </Text>
      ) : null}
    </span>
  )

  const productColumns = [
    {
      title: 'Product',
      key: 'product',
      render: (_: unknown, row: AgentProductAdminRow) => (
        <>
          <Text strong>{row.agents__name}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.agents__agent_key}
            {row.agents__client_name_raw && row.agents__client_name_raw !== row.agents__name
              ? ` · registered as “${row.agents__client_name_raw}”`
              : ''}
          </Text>
        </>
      )
    },
    {
      title: 'Source',
      dataIndex: 'agents__source',
      width: 140,
      render: (source: string) =>
        source === 'dcr' ? <Tag color='purple'>Self-registered</Tag> : <Tag>Seeded</Tag>
    },
    {
      title: (
        <Tooltip title='Tool calls per minute allowed per grant of this product. 0 = uncapped. A refused call still counts, so an agent cannot probe under the cap.'>
          Requests / min
        </Tooltip>
      ),
      dataIndex: 'agents__requests_per_minute',
      width: 170,
      render: (v: number, row: AgentProductAdminRow) =>
        capCell(v, row, 'requests_per_minute', 10000, 'Failed to update the rate cap')
    },
    {
      title: (
        <Tooltip title='Rows the aggregate read tools may return per grant per UTC day. 0 = uncapped.'>
          Rows / day
        </Tooltip>
      ),
      dataIndex: 'agents__daily_row_limit',
      width: 180,
      render: (v: number, row: AgentProductAdminRow) =>
        capCell(v, row, 'daily_row_limit', 100000000, 'Failed to update the daily row cap')
    },
    {
      title: (
        <Tooltip title='Live connections to this product. Each one gets its own budget against the caps.'>
          Connections
        </Tooltip>
      ),
      dataIndex: 'prog__active_grant_count',
      width: 120,
      render: (v: number) =>
        v > 0 ? <Tag color='blue'>{v}</Tag> : <Text type='secondary'>—</Text>
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 140,
      render: (_: unknown, row: AgentProductAdminRow) =>
        row.agents__is_effective === false ? (
          <Tooltip title={`Shadowed by the ${data.resolvedEnvironment} row for this product, so it has no effect here.`}>
            <Tag>Shadowed</Tag>
          </Tooltip>
        ) : row.agents__is_environment_override ? (
          <Tag color='purple'>{row.agents__environment}</Tag>
        ) : (
          <Text type='secondary'>All environments</Text>
        )
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_: unknown, row: AgentProductAdminRow) =>
        row.agents__enabled ? <Tag color='green'>Approved</Tag> : <Tag color='gold'>Pending</Tag>
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      // The button pair, not a Switch: disabling a product cuts every user of it
      // within the token TTL, and a Popconfirm wrapped around a Switch
      // double-fires (the Switch's own onChange still runs on the intercepted
      // click). Wording inherited from the retired Agent products tab.
      render: (_: unknown, row: AgentProductAdminRow) =>
        row.agents__enabled ? (
          <Popconfirm
            title='Disable this agent product?'
            description='Every user of this product loses access within minutes (no new sessions mint; live tokens expire).'
            okText='Disable'
            okButtonProps={{ danger: true }}
            onConfirm={() =>
              run(
                () => updateAgentProduct(token(), row.agents__agent_key, { enabled: false }),
                'Failed to disable the product',
                true
              )
            }
          >
            <Button danger size='small'>
              Disable
            </Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title='Approve this agent product?'
            description='One-time approval: users can then connect it themselves via their own consent + grant.'
            okText='Approve'
            onConfirm={() =>
              run(
                () => updateAgentProduct(token(), row.agents__agent_key, { enabled: true }),
                'Failed to approve the product',
                true
              )
            }
          >
            <Button type='primary' size='small'>
              Approve
            </Button>
          </Popconfirm>
        )
    }
  ]

  // ---- tools --------------------------------------------------------------
  // Only the caps that apply to this tool's kind are offered. The whitelist is
  // six keys but real data is one or two per tool, and rendering six numeric
  // columns would imply min_query_len means something on schedule_of_investments.
  const capSpecsFor = (row: AgentToolAdminRow | null): JsonFieldSpec[] => {
    if (!row) return []
    const applicable = Array.isArray(row.prog__applicable_cap_keys)
      ? (row.prog__applicable_cap_keys as string[])
      : null
    return (data.capKeys as AgentConfigValueKey[])
      .filter((k) => !applicable || applicable.includes(k.agent_config_value_keys__value_key))
      .map((k) => ({
        key: k.agent_config_value_keys__value_key,
        label: k.agent_config_value_keys__label,
        type: 'number',
        min: k.agent_config_value_keys__min_value ?? undefined,
        max: k.agent_config_value_keys__max_value ?? undefined,
        hint: k.agent_config_value_keys__description ?? undefined
      }))
  }

  const saveCaps = async (next: Record<string, unknown>): Promise<void> => {
    if (!capsRow) return
    setSaving(true)
    const ok = await run(
      () => updateAgentTool(token(), capsRow.agent_tools__tool_key, { value_caps: next }),
      'Failed to save the tool caps'
    )
    setSaving(false)
    if (ok) setCapsRow(null)
  }

  const toolColumns = [
    {
      title: 'Tool',
      key: 'tool',
      render: (_: unknown, row: AgentToolAdminRow) => (
        <>
          <Text strong>{row.agent_tools__name || row.agent_tools__tool_key}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.agent_tools__tool_key}
          </Text>
        </>
      )
    },
    {
      title: 'Kind',
      dataIndex: 'agent_tools__kind',
      width: 100,
      render: (v: string) => (v === 'write' ? <Tag color='purple'>Write</Tag> : <Tag>Read</Tag>)
    },
    {
      title: (
        <Tooltip title='Per-call limits. The gateway takes the smaller of what the agent asked for and the cap, then flags the response truncated.'>
          Caps
        </Tooltip>
      ),
      key: 'caps',
      width: 320,
      render: (_: unknown, row: AgentToolAdminRow) => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
          onClick={() => setCapsRow(row)}
        >
          {capsSummary(row.agent_tools__value_caps) || 'No caps'}
        </button>
      )
    },
    {
      title: (
        <Tooltip title='A disabled tool is refused for agent-origin requests even if a grant scopes it.'>
          Enabled
        </Tooltip>
      ),
      dataIndex: 'agent_tools__enabled',
      width: 100,
      render: (v: boolean, row: AgentToolAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () => updateAgentTool(token(), row.agent_tools__tool_key, { enabled: checked }),
              'Failed to update the tool',
              true
            )
          }
        />
      )
    }
  ]

  // ---- usage --------------------------------------------------------------
  const usageColumns = [
    {
      title: 'Connection',
      key: 'grant',
      render: (_: unknown, row: { prog__user_name?: string; agent_usage_counters__agent_key?: string }) => (
        <>
          <Text>{row.prog__user_name || 'Unknown user'}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.agent_usage_counters__agent_key}
          </Text>
        </>
      )
    },
    {
      title: 'Status',
      dataIndex: 'prog__grant_status',
      width: 110,
      render: (v: string) =>
        v === 'active' ? (
          <Tag color='green'>Active</Tag>
        ) : v ? (
          <Tag>{v === 'revoked' ? 'Revoked' : 'Expired'}</Tag>
        ) : (
          <Text type='secondary'>—</Text>
        )
    },
    {
      title: 'Requests',
      dataIndex: 'prog__requests_today',
      width: 120,
      render: (v: number) => <Text>{compactNumber(v)}</Text>
    },
    {
      title: 'Peak / min',
      dataIndex: 'prog__peak_requests_per_minute',
      width: 120,
      render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text>
    },
    {
      title: 'Rows returned',
      dataIndex: 'prog__rows_returned_today',
      width: 140,
      render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text>
    }
  ]

  const usageTotals = useMemo(
    () =>
      usage.by_agent.reduce(
        (acc, a) => ({
          requests: acc.requests + Number(a.prog__requests_today ?? 0),
          rows: acc.rows + Number(a.prog__rows_returned_today ?? 0)
        }),
        { requests: 0, rows: 0 }
      ),
    [usage.by_agent]
  )

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      <FormSection
        title='Products'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Caps apply per connection, not per product
            </Text>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        <Table
          size='small'
          rowKey='agents__id'
          pagination={false}
          dataSource={data.products}
          columns={productColumns}
          scroll={{ x: 1200 }}
        />
      </FormSection>

      <FormSection
        title='Tools'
        titleColor='green'
        scrollable={false}
        loading={loading}
        headerActions={
          <Text type='secondary' style={{ fontSize: 12 }}>
            Only the caps that apply to each tool are offered
          </Text>
        }
      >
        <Table
          size='small'
          rowKey='agent_tools__id'
          pagination={false}
          dataSource={data.tools}
          columns={toolColumns}
        />
      </FormSection>

      <FormSection
        title='Usage today'
        titleColor='gray'
        scrollable={false}
        loading={loading}
        headerActions={
          <Text type='secondary' style={{ fontSize: 12 }}>
            {compactNumber(usageTotals.requests)} requests ·{' '}
            {compactNumber(usageTotals.rows)} rows since 00:00 UTC
          </Text>
        }
      >
        <Table
          size='small'
          rowKey='agent_usage_counters__grant_key'
          pagination={false}
          dataSource={usage.by_grant}
          columns={usageColumns}
          locale={{ emptyText: 'No agent traffic yet today' }}
        />
      </FormSection>

      <JsonFieldsModal
        open={capsRow !== null}
        title={`Caps · ${capsRow?.agent_tools__tool_key ?? ''}`}
        description='Applied to every call of this tool, for every connected agent. Leave a field blank for no limit.'
        fields={capSpecsFor(capsRow)}
        value={(capsRow?.agent_tools__value_caps as Record<string, unknown>) || {}}
        saving={saving}
        onCancel={() => setCapsRow(null)}
        onSave={saveCaps}
      />
    </div>
  )
}
